import { useCallback, useEffect, useMemo, useState } from "react";
import Select from "../../components/Select.jsx";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import useFetch from "../../hooks/useFetch.js";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { User, Users } from "lucide-react";

const OnBehalfOf = ({
  setBehalfOfFields,
  setShowFields,
  formValidate,
  demographics,
  setDemographics,
  edit,

  // Subscription props
  isSubscriptionUser = false,
  setRepresentativeRelationId,
}) => {
  const { id } = useSelector((state) => state.mainFun.user);

  // =====================================================
  // 1. States
  // =====================================================

  const [caseFor, setCaseFor] = useState(
    isSubscriptionUser ? "someone_else" : ""
  );

  const [onBehalf, setOnBehalf] = useState({
    sub_profile: "",
    behalf_of: "",
  });

  const [behalfOfData, setBehalfOfData] = useState([]);
  const [relatedProfiles, setRelatedProfiles] = useState([]);

  const [searchParams] = useSearchParams();

  const requestedProfileId = searchParams.get("id");

  const { t } = useTranslation();
  const { fetchData } = useFetch({});

  // =====================================================
  // 2. Patient selection tabs
  // =====================================================

  const caseForTabs = [
    {
      value: "me",
      label: t("demographics.myself"),
    },
    {
      value: "someone_else",
      label: t("demographics.someone_else"),
    },
  ];

  // =====================================================
  // 3. Get related patient profiles
  // =====================================================

  const getSubProfiles = async () => {
    try {
      const result = await fetchData({
        url: "patient/profile/get",
        body: {},
      });

      if (result?.result?.patient_profiles?.length > 0) {
        setRelatedProfiles(
          result.result.patient_profiles.map((e) => ({
            value: e.id,
            label: e.name,
          }))
        );
      } else {
        setRelatedProfiles([]);
      }
    } catch (error) {
      console.error("Error fetching patient profiles:", error);
    }
  };

  // =====================================================
  // 4. Get relationship options
  // =====================================================

  const getBehalfOfData = async () => {
    try {
      const result = await fetchData({
        url: "patient/relations/get",
        body: {},
      });

      if (result?.result?.patient_relations) {
        setBehalfOfData(
          result.result.patient_relations.map((e) => ({
            value: e.id,
            label: e.name,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching relationships:", error);
    }
  };

  // =====================================================
  // 5. Find Representative relationship dynamically
  // =====================================================

  const representativeOption = useMemo(() => {
    return behalfOfData.find((relation) => {
      const relationName = String(
        relation.label || ""
      ).trim().toLowerCase();

      return (
        relationName === "representative" ||
        relationName === "representitive"
      );
    });
  }, [behalfOfData]);

  // =====================================================
  // 6. Load available profiles and relationships
  // =====================================================

  useEffect(() => {
    getBehalfOfData();
    getSubProfiles();
  }, []);

  // =====================================================
  // 7. Initialize patient selection
  // =====================================================

  useEffect(() => {
    // Subscription users must create cases for someone else
    if (isSubscriptionUser) {
      setCaseFor("someone_else");

      // Allow selecting an existing related patient,
      // but never the logged-in user's own profile.
      if (
        requestedProfileId &&
        String(requestedProfileId) !== String(id)
      ) {
        setOnBehalf((prev) => ({
          ...prev,
          sub_profile: requestedProfileId,
        }));
      } else {
        setOnBehalf((prev) => ({
          ...prev,
          sub_profile: "",
        }));
      }

      return;
    }

    // Normal user behavior
    if (
      requestedProfileId &&
      String(id) === String(requestedProfileId)
    ) {
      setCaseFor("me");
    } else if (requestedProfileId) {
      setCaseFor("someone_else");

      setOnBehalf((prev) => ({
        ...prev,
        sub_profile: requestedProfileId,
      }));
    } else {
      setCaseFor("");
    }
  }, [isSubscriptionUser, id, requestedProfileId]);

  // =====================================================
  // 8. Automatically select Representative
  // =====================================================

  useEffect(() => {
    if (!isSubscriptionUser || !representativeOption) {
      return;
    }

    const representativeId = representativeOption.value;

    // Save ID in parent component
    if (setRepresentativeRelationId) {
      setRepresentativeRelationId(representativeId);
    }

    // Update OnBehalf state
    setOnBehalf((prev) => {
      if (
        prev.behalf_of?.value === representativeId
      ) {
        return prev;
      }

      return {
        ...prev,
        behalf_of: representativeOption,
      };
    });

    // Update demographics
    setDemographics((prev) => {
      if (
        String(prev?.relation_id) === String(representativeId)
      ) {
        return prev;
      }

      return {
        ...(prev || {}),
        relation_id: representativeId,
      };
    });

    // Update React Hook Form value
    if (formValidate?.setValue) {
      formValidate.setValue(
        "relation_id",
        representativeId,
        {
          shouldValidate: true,
        }
      );
    }
  }, [
    isSubscriptionUser,
    representativeOption,
    demographics?.relation_id,
    formValidate?.setValue,
    setDemographics,
    setRepresentativeRelationId,
  ]);

  // =====================================================
  // 9. Get patient demographics
  // =====================================================

  const getDemographics = useCallback(async () => {
    const body = {};

    if (onBehalf?.sub_profile) {
      body.sub_profile = onBehalf.sub_profile;
    }

    try {
      const result = await fetchData({
        url: "patient/demographics/get",
        body: { params: body },
        loading: true,
      });

      if (result?.result?.patient?.gender) {
        const patientData = result.result.patient;

        // Subscription users must always be Representative
        if (isSubscriptionUser && representativeOption) {
          patientData.relation_id = representativeOption.value;
        }

        setDemographics(patientData);
      }
    } catch (error) {
      console.error("Error fetching demographics:", error);
    }
  }, [
    onBehalf?.sub_profile,
    caseFor,
    isSubscriptionUser,
    representativeOption,
  ]);

  // =====================================================
  // 10. Patient first and last name fields
  // =====================================================

  const behalfOfFields = [
    {
      title: t("patient first name"),
      placeholder: t("type", {
        val: "patient first name",
      }),
      name: "first_name",
      colValue: "col-6",
      value: demographics?.first_name,
      order: 0,
    },
    {
      title: t("patient last name"),
      placeholder: t("type", {
        val: "patient last name",
      }),
      name: "last_name",
      colValue: "col-6",
      value: demographics?.last_name,
      order: 0,
    },
  ];

  // =====================================================
  // 11. Configure additional fields
  // =====================================================

  useEffect(() => {
    if (caseFor === "someone_else") {
      setBehalfOfFields({
        fields: behalfOfFields,
        validateFields: [
          {
            name: "first_name",
            validate: "name",
          },
          {
            name: "last_name",
            validate: "name",
          },
        ],
      });
    } else {
      setBehalfOfFields({
        fields: [],
        validateFields: [],
      });
    }
  }, [caseFor, demographics]);

  // =====================================================
  // 12. Load demographics when patient changes
  // =====================================================

  useEffect(() => {
    setDemographics({});

    if (
      caseFor === "me" ||
      (
        caseFor === "someone_else" &&
        onBehalf?.sub_profile
      )
    ) {
      getDemographics();
    }
  }, [onBehalf?.sub_profile, caseFor]);

  // =====================================================
  // 13. Control demographics fields visibility
  // =====================================================

  useEffect(() => {
    if (caseFor === "someone_else") {
      if (
        onBehalf?.sub_profile ||
        (
          onBehalf?.behalf_of &&
          !onBehalf?.sub_profile
        )
      ) {
        setShowFields(true);
      } else {
        setShowFields(false);
      }
    } else if (caseFor === "me") {
      setBehalfOfFields({
        fields: [],
        validateFields: [],
      });

      setShowFields(true);
    } else {
      setShowFields(false);
    }
  }, [onBehalf, caseFor]);

  // =====================================================
  // 14. Handle related patient selection
  // =====================================================

  const onRelationChange = (e) => {
    if (e) {
      // Prevent subscription users from selecting themselves
      if (
        isSubscriptionUser &&
        String(e.value) === String(id)
      ) {
        return;
      }

      setOnBehalf((prev) => ({
        ...prev,
        sub_profile: e.value,
      }));
    } else {
      setOnBehalf((prev) => ({
        ...prev,
        sub_profile: "",
      }));

      setDemographics({});
    }
  };

  // =====================================================
  // 15. Handle relationship selection
  // =====================================================

  const onBehalfChange = (e) => {
    // Subscription users must always be Representative
    if (isSubscriptionUser) {
      if (representativeOption) {
        setOnBehalf((prev) => ({
          ...prev,
          behalf_of: representativeOption,
        }));

        formValidate?.setValue?.(
          "relation_id",
          representativeOption.value,
          { shouldValidate: true }
        );
      }

      return;
    }

    // Normal user behavior
    setOnBehalf((prev) => ({
      ...prev,
      behalf_of: e,
    }));
  };

  // =====================================================
  // 16. Handle patient type selection
  // =====================================================

  const onCaseForChange = (value) => {
    // Subscription users cannot select Myself
    if (
      isSubscriptionUser &&
      value === "me"
    ) {
      return;
    }

    setCaseFor(value);

    setOnBehalf({
      sub_profile: "",
      behalf_of: isSubscriptionUser
        ? representativeOption || ""
        : "",
    });
  };

  // =====================================================
  // 17. Restrict relationships for subscription users
  // =====================================================

  const availableRelations = isSubscriptionUser
    ? representativeOption
      ? [representativeOption]
      : []
    : behalfOfData;

  // =====================================================
  // 18. Render
  // =====================================================

  return (
    <div className="d-flex flex-column gap-4">

      {/* Title */}
      <p>
        {t(
          edit
            ? "demographics.edit_demo"
            : "demographics.title"
        )}:
      </p>

      {/* Patient Selection */}
      <div className="d-flex gap-3 justify-content-center">

        {caseForTabs.map((e) => {
          const disabled =
            isSubscriptionUser &&
            e.value === "me";

          return (
            <button
              key={e.value}
              className={clsx(
                "rounded-3 p-3 d-flex w-50 flex-column align-items-center gap-1 border text-upper-first primary-txt text-center",
                {
                  "bg-dark-green border-0 text-white":
                    caseFor === e.value,

                  "bg-transparent grey-border":
                    caseFor !== e.value,

                  "opacity-50":
                    disabled,
                }
              )}
              onClick={() => onCaseForChange(e.value)}
              type="button"
              disabled={disabled}
            >
              {e.value === "me" ? (
                <User size={28} />
              ) : (
                <Users size={28} />
              )}

              <span>{e.label}</span>
            </button>
          );
        })}
      </div>

      {/* Someone Else Fields */}
      {caseFor === "someone_else" && (
        <>

          {/* Related Patient Profiles */}
          {relatedProfiles.length > 0 && (
            <Select
              title={t("demographics.related")}
              isClearable={true}
              data={
                isSubscriptionUser
                  ? relatedProfiles.filter(
                      (profile) =>
                        String(profile.value) !== String(id)
                    )
                  : relatedProfiles
              }
              name="sub_profile"
              value={onBehalf?.sub_profile}
              setValue={formValidate?.setValue}
              control={formValidate?.control}
              onChange={onRelationChange}
            />
          )}

          {/* On Behalf Of */}
          <Select
            title={t("demographics.on_behalf")}
            name="relation_id"
            data={availableRelations}
            control={formValidate?.control}
            setValue={formValidate?.setValue}
            value={
              isSubscriptionUser
                ? representativeOption?.value || ""
                : demographics?.relation_id
            }
            onChange={onBehalfChange}
            isClearable={!isSubscriptionUser}
            isDisabled={isSubscriptionUser}
            disabled={isSubscriptionUser}
          />

        </>
      )}
    </div>
  );
};

export default OnBehalfOf;